import { useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';

interface ExportOptions {
  title: string;
  subtitle?: string;
  filenameSlug?: string;
}

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Captures a report container exactly as it appears on screen and turns it into
 * a high quality multi-page PDF (screen "scroll" becomes sequential pages).
 */
export const useReportPdfExport = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportElementToPdf = useCallback(
    async (element: HTMLElement | null, options: ExportOptions) => {
      if (!element) {
        toast({ title: 'Erro', description: 'Conteúdo do relatório não encontrado.', variant: 'destructive' });
        return;
      }

      setIsExporting(true);
      element.classList.add('pdf-exporting');

      // Give the browser a frame to apply the expansion styles
      await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 120)));

      try {
        const bgColor =
          getComputedStyle(document.body).backgroundColor || '#ffffff';

        const scale = Math.min(3, Math.max(2, (window.devicePixelRatio || 1) * 2));

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          backgroundColor: bgColor,
          logging: false,
          windowWidth: element.scrollWidth,
          width: element.scrollWidth,
          height: element.scrollHeight,
        });

        const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4', compress: true });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const marginX = 24;
        const headerHeight = 46;
        const footerHeight = 26;
        const contentWidth = pageWidth - marginX * 2;
        const contentHeight = pageHeight - headerHeight - footerHeight;

        // Ratio between canvas pixels and PDF points
        const pxPerPt = canvas.width / contentWidth;
        const sliceHeightPx = Math.floor(contentHeight * pxPerPt);

        const requestedBy = user?.user_metadata?.display_name || user?.email || 'Usuário';
        const generatedAt = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

        const totalPages = Math.max(1, Math.ceil(canvas.height / sliceHeightPx));

        for (let page = 0; page < totalPages; page++) {
          if (page > 0) pdf.addPage();

          const sourceY = page * sliceHeightPx;
          const sourceHeight = Math.min(sliceHeightPx, canvas.height - sourceY);

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sourceHeight;
          const ctx = sliceCanvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight
            );
          }

          // Header
          pdf.setTextColor(30, 41, 59);
          pdf.setFontSize(14);
          pdf.text(options.title, marginX, 26);
          pdf.setFontSize(9);
          pdf.setTextColor(100, 116, 139);
          if (options.subtitle) {
            pdf.text(options.subtitle, marginX, 38);
          }
          pdf.text(
            `Solicitado por: ${requestedBy} · ${generatedAt} (UTC-3)`,
            pageWidth - marginX,
            26,
            { align: 'right' }
          );

          // Content slice
          const imgHeightPt = sourceHeight / pxPerPt;
          pdf.addImage(
            sliceCanvas.toDataURL('image/png'),
            'PNG',
            marginX,
            headerHeight,
            contentWidth,
            imgHeightPt,
            undefined,
            'FAST'
          );

          // Footer
          pdf.setFontSize(8);
          pdf.setTextColor(100, 116, 139);
          pdf.text(
            `Página ${page + 1} de ${totalPages}`,
            pageWidth - marginX,
            pageHeight - 12,
            { align: 'right' }
          );
        }

        const slug = options.filenameSlug || slugify(options.title);
        pdf.save(`relatorio_${slug}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);

        toast({ title: 'Sucesso', description: 'PDF exportado com sucesso!' });
      } catch (error) {
        console.error('Erro ao exportar PDF do relatório:', error);
        toast({ title: 'Erro', description: 'Não foi possível gerar o PDF.', variant: 'destructive' });
      } finally {
        element.classList.remove('pdf-exporting');
        setIsExporting(false);
      }
    },
    [user, toast]
  );

  return { exportElementToPdf, isExporting };
};
